/* ============================================================
   ALAIN PHILIPPE · THE REVEAL ENGINE
   docs/gallery-standard.md §1.6.

   Generalised in August 2026 from the collections walk's own engine, which
   drove its reveals off a horizontal world's x offset where this drives them
   from an IntersectionObserver; the tier structure, the class names and the
   contract with the stylesheet came across unchanged. That walk is retired
   (9 September 2026) and this is the whole reveal engine now.

   THE CONTRACT, and the reason this file is small.

     The revealed state is the resting CSS state. Nothing in
     assets/ap-tokens.css hides anything until `.is-anim` is on <html>. So:

       no JavaScript          -> no .is-anim -> a finished composition
       JavaScript that failed -> the net below removes .is-anim -> finished
       prefers-reduced-motion -> .is-anim withheld -> finished
       Shopify theme editor   -> .is-anim withheld -> finished, and selectable

     This file's whole job is therefore: decide whether choreography is
     allowed, and if it is, tell each unit when it has arrived.

   THE MARKUP CONTRACT

     data-ap-reveal        a reveal ROOT. Gets .is-anim when motion is
                           permitted, and only after at least one group inside
                           it has been registered, so the class can never
                           arrive with nothing to drive it.
     data-ap-reveal-group  a unit inside a root that opens as one. Gets .is-in
                           when it arrives. A root with no groups is its own
                           group, which is the short form:
                             <div class="ap-fade" data-ap-reveal>
     .ap-mask              a line of type rises inside its own line box
     .ap-curtain           a rectangle of the surface's ground slides off
     .ap-fade              cut-outs and soft elements
     .ap-d1 … .ap-d6       the delay ladder, 0.12s steps

   TWO SCOPES FOR .is-anim, ON PURPOSE

     <html>  carries it for the house tier in assets/ap-tokens.css and for the
             legacy .fade-up blocks that have not been rebuilt yet.
     a root  carries it too, so a section stylesheet can scope every hidden
             state to its own root and have zero blast radius, which is what
             assets/ap-maison.css and assets/ap-boutiques.css both do. Both
             gates open and close together, from the one motion decision, so a
             page can never be half choreographed.

   The <html> class is stamped in <head> by layout/theme.liquid rather than
   here, so the hidden state is in place before first paint and nothing flashes
   composed and then empties. This file re-applies it defensively, adds the
   per-root class, keeps both in step with a live change to the motion
   preference, and owns the safety net.
   ============================================================ */
(function () {
  'use strict';

  var doc = document.documentElement;

  /* The editor never gets choreography. A section that reloads mid-edit would
     otherwise reveal from a hidden state on every keystroke; the rule that
     the editor gets the composed page was settled on the walk's port and it
     is the house rule now. */
  function inEditor() {
    return !!(window.Shopify && window.Shopify.designMode);
  }

  var mq = null;
  try {
    mq = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
  } catch (e) {
    mq = null;
  }

  function motionAllowed() {
    return !inEditor() && !(mq && mq.matches);
  }

  var io = null;
  var roots = [];

  function reveal(el) {
    if (el.classList.contains('is-in')) return;
    el.classList.add('is-in');
  }

  function ensureObserver() {
    if (io || !window.IntersectionObserver) return io;
    io = new window.IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        if (entries[i].isIntersecting) {
          reveal(entries[i].target);
          io.unobserve(entries[i].target);
        }
      }
    }, {
      /* the unit opens as it arrives, not once it is fully on screen: a tall
         unit would otherwise never reach a high threshold at all */
      rootMargin: '0px 0px -12% 0px',
      threshold: 0.08
    });
    return io;
  }

  function groupsOf(root) {
    var groups = root.querySelectorAll('[data-ap-reveal-group]');
    if (groups.length) return Array.prototype.slice.call(groups);
    /* the short form: a root with no groups inside it is its own group */
    return [root];
  }

  function bindRoot(root) {
    if (root.dataset.apRevealBound === '1') return;
    root.dataset.apRevealBound = '1';

    var groups = groupsOf(root);
    if (!groups.length) return;

    roots.push(root);
    for (var i = 0; i < groups.length; i++) io.observe(groups[i]);

    /* the class arrives only now, with something registered to drive it. A
       root that claimed .is-anim with no groups would hide its own contents
       for good, which is the fault this whole engine exists to prevent. */
    root.classList.add('is-anim');
  }

  function observe(scope) {
    if (!ensureObserver()) {
      revealAll(scope);
      return;
    }
    var found = (scope || document).querySelectorAll('[data-ap-reveal]');
    for (var i = 0; i < found.length; i++) bindRoot(found[i]);
  }

  /* Every group open at once, with no class anywhere that could hide anything.
     The answer when there is no IntersectionObserver: the finished page, which
     is the same answer as no script at all. */
  function revealAll(scope) {
    var s = scope || document;
    var all = s.querySelectorAll('[data-ap-reveal], [data-ap-reveal-group]');
    for (var i = 0; i < all.length; i++) reveal(all[i]);
  }

  /* Everything visible, permanently, with no transition to play. Used when
     motion is refused after the head stamp has already run. */
  function composeStatic() {
    doc.classList.remove('is-anim');
    for (var i = 0; i < roots.length; i++) {
      roots[i].classList.remove('is-anim');
      /* released, so a visitor who turns motion back on gets it back rather
         than a page that is permanently static because it was bound once */
      roots[i].dataset.apRevealBound = '';
    }
    roots = [];
    if (io) {
      io.disconnect();
      io = null;
    }
  }

  function start() {
    if (!motionAllowed() || !window.IntersectionObserver) {
      /* strip every choreography class first, so nothing anywhere is left
         hidden, and only then open the groups */
      composeStatic();
      revealAll(document);
      return;
    }
    doc.classList.add('is-anim');
    observe(document);
  }

  if (mq) {
    var onPref = function () {
      if (motionAllowed()) start();
      else composeStatic();
    };
    if (typeof mq.addEventListener === 'function') mq.addEventListener('change', onPref);
    else if (typeof mq.addListener === 'function') mq.addListener(onPref);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  /* late arrivals: a section reloaded in the editor, or markup injected by
     another script. The editor branch of start() has already refused motion,
     so this only ever binds new units on a live page. */
  document.addEventListener('shopify:section:load', function (e) {
    if (!motionAllowed()) return;
    observe(e.target);
  });

  window.APReveal = {
    observe: observe,
    reveal: reveal,
    revealAll: revealAll,
    motionAllowed: motionAllowed
  };

  /* THE SAFETY NET, and the reason the head stamp is safe to write.

     The stamp puts the document into the hidden state before this file has
     loaded. If the file never arrives, or throws anywhere above this line, the
     page would be stuck at opacity 0 for good, which is precisely the fault
     this engine exists to fix.

     So the flag is cleared LAST, not first. Everything above has to have run
     for the engine to be considered present. layout/theme.liquid checks the
     flag on window load and takes .is-anim straight back off if it is still
     set, which leaves the visitor the same finished page a visitor with no
     JavaScript gets. */
  window.__apRevealPending = false;
})();
